'use client';
import Cookies from 'js-cookie';
import { useEffect, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { setGAConfig } from '../utils';
import { useAppEnvironment } from './useAppEnvironment';
import {
  useTrackAppOrPwaId,
  useTrackingUserParamsStore,
} from '@/modules/userTrackingParams';
import { useCommonStore } from '@/store';

/**
 * @description This hook is used to set the Google Analytics configuration.
 * It retrieves the tracking parameters from the user tracking store and the user ID from the common store.
 * It also determines the platform (web or PWA) and sets the Google Analytics configuration accordingly.
 */
export const useGAConfigHook = () => {
  const { stag, campaign_id, promo_id } = useTrackingUserParamsStore(
    useShallow((state) => ({
      stag: state.stag,
      campaign_id: state.campaign_id,
      promo_id: state.promo_id,
    }))
  );

  const { app_id, pwa_id } = useTrackAppOrPwaId();
  const paramsFromCookies = Cookies.get('tracking-user-params');
  const params: Record<string, unknown> = paramsFromCookies
    ? JSON.parse(paramsFromCookies)
    : null;

  const user_id = useCommonStore.use.user()?.id;
  const platform = useAppEnvironment();

  const isStandalone = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true
    );
  }, []);

  useEffect(() => {
    setGAConfig({
      stag: stag || params?.stag,
      campaign_id,
      promo_id,
      user_id,
      platform,
      ...(isStandalone && pwa_id ? { pwa_id } : {}),
      app_id,
    });
  }, [
    stag,
    campaign_id,
    promo_id,
    user_id,
    platform,
    pwa_id,
    app_id,
    isStandalone,
  ]);
};
